import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUp, Calendar, Clock, ChevronDown } from 'lucide-react';
import SEOHead from '../../components/SEOHead';
import BlogNotFound from '../../components/BlogNotFound';
import { getBlogBySlug } from '../../data/blogs';

const Blog22022026 = () => {
  const [activeSection, setActiveSection] = useState('');
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [tocOpen, setTocOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const blog = getBlogBySlug('blog-22022026');

  useEffect(() => {
    setIsDesktop(window.innerWidth >= 1100);

    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 500);

      const sections = document.querySelectorAll('h2[id]');
      let current = '';

      sections.forEach(section => {
        const sectionTop = section.getBoundingClientRect().top;
        if (sectionTop <= 150) {
          current = section.getAttribute('id') || '';
        }
      });

      setActiveSection(current);
    };

    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1100);
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const yOffset = -120;
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
      setTocOpen(false);
    }
  };

  const tableOfContents = [];

  const faqData = [
    {
        "question": "What is this technology and how does it work?",
        "answer": "This technology represents a significant advancement in the field, offering innovative solutions to common challenges through modern approaches and proven methodologies."
    },
    {
        "question": "Who can benefit from implementing this solution?",
        "answer": "Organizations of all sizes can benefit, particularly those looking to improve efficiency, reduce costs, and enhance their competitive advantage through technological innovation."
    },
    {
        "question": "What are the main challenges in implementation?",
        "answer": "Key challenges include initial setup complexity, integration with existing systems, and ensuring proper training. However, with proper planning and support, these can be effectively managed."
    },
    {
        "question": "What ROI can be expected?",
        "answer": "While results vary by organization, typical implementations show significant improvements in operational efficiency, cost reduction, and enhanced capabilities within the first year."
    }
];

  const tldrPoints = [
    "Comprehensive guide to modern implementation strategies",
    "Best practices for rapid deployment and measurable results",
    "Real-world examples from successful enterprise implementations"
];

  const keyTakeaways = [
    "Fast implementation strategies deliver measurable ROI within weeks, not months",
    "Agile methodologies reduce time-to-production by 60-80% compared to traditional approaches",
    "Cloud-native architecture enables rapid scaling without infrastructure bottlenecks",
    "Automated workflows eliminate manual bottlenecks and accelerate delivery timelines",
    "Real-time analytics provide immediate insights for faster decision-making"
];

  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BlogPosting",
        "headline": blog?.title,
        "description": blog?.excerpt,
        "image": blog?.featuredImage,
        "datePublished": blog?.publishDate,
        "dateModified": blog?.publishDate,
        "author": {
          "@type": "Organization",
          "name": "Agility"
        },
        "publisher": {
          "@type": "Organization",
          "name": "Agility",
          "logo": {
            "@type": "ImageObject",
            "url": "https://www.agilitytech.ai/agility hex fonts.png"
          }
        },
        "keywords": blog?.seoKeywords.join(', '),
        "articleBody": "",
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": `https://www.agilitytech.ai/insights/${blog?.slug}`
        },
        "speakable": {
          "@type": "SpeakableSpecification",
          "cssSelector": [".tldr-section", ".key-takeaways-section"]
        }
      },
      {
        "@type": "HowTo",
        "name": blog?.title,
        "description": blog?.excerpt,
        "step": keyTakeaways.map((takeaway, index) => ({
          "@type": "HowToStep",
          "position": index + 1,
          "name": `Step ${index + 1}`,
          "text": takeaway
        }))
      },
      {
        "@type": "FAQPage",
        "mainEntity": faqData.map(faq => ({
          "@type": "Question",
          "name": faq.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.answer
          }
        }))
      }
    ]
  };

  if (!blog) return <BlogNotFound />;

  return (
    <div className="min-h-screen" style={{ background: '#050505' }}>
      <SEOHead
        title={`${blog?.title} | Agility Blog`}
        description={blog?.excerpt}
        keywords={blog?.seoKeywords.join(', ')}
        url={`https://www.agilitytech.ai/insights/${blog?.slug}`}
        image={blog?.featuredImage}
        type="article"
        article={true}
        author={blog?.author}
        publishedTime={new Date(blog?.publishDate || '').toISOString()}
        modifiedTime={new Date(blog?.publishDate || '').toISOString()}
        structuredData={structuredData}
      />

      {/* Blog Hero Section */}
      <section className="pt-32 pb-16" style={{ background: 'linear-gradient(to bottom, #0B0B0B, #050505)' }}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" style={{ maxWidth: '1200px' }}>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <div className="mb-6">
              <span style={{
                display: 'inline-block',
                padding: '0.5rem 1rem',
                background: 'rgba(20, 184, 166, 0.15)',
                color: '#14b8a6',
                borderRadius: '9999px',
                fontSize: '0.875rem',
                fontWeight: '500',
                border: '1px solid rgba(20, 184, 166, 0.3)'
              }}>
                {blog.category}
              </span>
            </div>

            <h1 style={{
              fontSize: 'clamp(2rem, 5vw, 3rem)',
              fontFamily: 'Sora, sans-serif',
              fontWeight: '700',
              color: '#ffffff',
              marginBottom: '1.5rem',
              lineHeight: '1.2',
              letterSpacing: '-0.02em'
            }}>
              {blog.title}
            </h1>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem', color: '#9ca3af', marginBottom: '2rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Calendar size={16} />
                <span style={{ fontSize: '0.875rem' }}>{new Date(blog.publishDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Clock size={16} />
                <span style={{ fontSize: '0.875rem' }}>{blog.publishDate}</span>
              </div>
            </div>

            <p style={{
              fontSize: 'clamp(1rem, 2.5vw, 1.25rem)',
              color: '#d1d5db',
              lineHeight: '1.75',
              marginBottom: '2rem'
            }}>
              {blog.excerpt}
            </p>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {blog.tags.map((tag, index) => (
                <span key={index} style={{
                  padding: '0.25rem 0.75rem',
                  background: '#121212',
                  color: '#14b8a6',
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem',
                  border: '1px solid #262626'
                }}>
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Main Content with Sidebar */}
      <section style={{ background: '#050505', paddingBottom: '5rem' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1.5rem', display: 'flex', minHeight: '100vh', gap: '3rem', flexDirection: isDesktop ? 'row' : 'column' }}>
          {/* Sidebar TOC - Desktop */}
          {isDesktop && (
            <div style={{
              position: 'sticky',
              top: '100px',
              height: 'fit-content',
              width: '260px',
              flexShrink: 0,
              alignSelf: 'flex-start',
              zIndex: 999
            }}>
              <h3 style={{
                color: '#ffffff',
                fontWeight: '600',
                marginBottom: '1rem',
                fontSize: '0.875rem',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>Table of Contents</h3>
              <nav style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {tableOfContents.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      fontSize: '0.875rem',
                      padding: '0.5rem 1rem',
                      borderRadius: '0.5rem',
                      transition: 'all 0.2s',
                      borderLeft: activeSection === item.id ? '2px solid #14b8a6' : '2px solid transparent',
                      background: activeSection === item.id ? 'rgba(20,184,166,0.15)' : 'transparent',
                      color: activeSection === item.id ? '#14b8a6' : '#9ca3af',
                      cursor: 'pointer',
                      border: 'none'
                    }}
                    onMouseEnter={(e) => {
                      if (activeSection !== item.id) {
                        e.currentTarget.style.color = '#d1d5db';
                        e.currentTarget.style.background = '#121212';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (activeSection !== item.id) {
                        e.currentTarget.style.color = '#9ca3af';
                        e.currentTarget.style.background = 'transparent';
                      }
                    }}
                  >
                    {item.title}
                  </button>
                ))}
              </nav>
            </div>
          )}

          {/* Mobile TOC */}
          {!isDesktop && (
            <div style={{
              width: '100%',
              marginBottom: '2rem'
            }}>
              <div style={{
                background: '#121212',
                borderRadius: '12px',
                border: '1px solid #262626',
                padding: '1rem'
              }}>
                <button
                  onClick={() => setTocOpen(!tocOpen)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    background: 'none',
                    border: 'none',
                    color: '#ffffff',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  <span>Table of Contents</span>
                  <ChevronDown style={{
                    transform: tocOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.3s'
                  }} />
                </button>
                {tocOpen && (
                  <nav style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {tableOfContents.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => scrollToSection(item.id)}
                        style={{
                          width: '100%',
                          textAlign: 'left',
                          padding: '0.5rem 1rem',
                          background: 'none',
                          border: 'none',
                          color: activeSection === item.id ? '#14b8a6' : '#9ca3af',
                          fontSize: '0.875rem',
                          cursor: 'pointer'
                        }}
                      >
                        {item.title}
                      </button>
                    ))}
                  </nav>
                )}
              </div>
            </div>
          )}

          {/* Article Content */}
          <div style={{ flex: 1 }}>
            <article style={{ maxWidth: '800px', margin: '0 auto', width: '100%' }}>

              {/* TL;DR Section - AEO Optimized */}
              <div className="tldr-section" style={{
                background: 'linear-gradient(135deg, rgba(20, 184, 166, 0.1), rgba(20, 184, 166, 0.05))',
                borderLeft: '4px solid #14b8a6',
                borderRadius: '12px',
                padding: '2rem',
                marginBottom: '3rem',
                border: '1px solid rgba(20, 184, 166, 0.2)'
              }}>
                <h2 style={{
                  color: '#14b8a6',
                  fontSize: 'clamp(1.25rem, 3vw, 1.5rem)',
                  fontFamily: 'Sora, sans-serif',
                  fontWeight: '700',
                  marginBottom: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <span style={{
                    background: '#14b8a6',
                    color: '#000000',
                    padding: '0.25rem 0.75rem',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    fontWeight: '700'
                  }}>TL;DR</span>
                  Quick Summary
                </h2>
                <ul style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.75rem'
                }}>
                  {tldrPoints.map((point, idx) => (
                    <li key={idx} style={{
                      color: '#e5e7eb',
                      fontSize: 'clamp(0.95rem, 2vw, 1.05rem)',
                      lineHeight: '1.7',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '0.75rem'
                    }}>
                      <span style={{
                        color: '#14b8a6',
                        fontWeight: '700',
                        fontSize: '1.25rem',
                        lineHeight: '1'
                      }}>•</span>
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </div>

              

              {/* Key Takeaways Section - AEO Optimized */}
              <div className="key-takeaways-section" style={{
                background: 'linear-gradient(135deg, rgba(20, 184, 166, 0.1), rgba(20, 184, 166, 0.05))',
                borderLeft: '4px solid #14b8a6',
                borderRadius: '12px',
                padding: '2rem',
                marginTop: '3rem',
                marginBottom: '3rem',
                border: '1px solid rgba(20, 184, 166, 0.2)'
              }}>
                <h2 style={{
                  color: '#14b8a6',
                  fontSize: 'clamp(1.25rem, 3vw, 1.5rem)',
                  fontFamily: 'Sora, sans-serif',
                  fontWeight: '700',
                  marginBottom: '1.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <span style={{
                    background: '#14b8a6',
                    color: '#000000',
                    padding: '0.25rem 0.75rem',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    fontWeight: '700'
                  }}>⚡</span>
                  Key Takeaways - Fast Implementation Insights
                </h2>
                <ul style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1rem'
                }}>
                  {keyTakeaways.map((takeaway, idx) => (
                    <li key={idx} style={{
                      color: '#ffffff',
                      fontSize: 'clamp(0.95rem, 2vw, 1.05rem)',
                      lineHeight: '1.7',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '1rem',
                      background: 'rgba(255, 255, 255, 0.03)',
                      padding: '1rem',
                      borderRadius: '8px',
                      border: '1px solid rgba(20, 184, 166, 0.1)'
                    }}>
                      <span style={{
                        color: '#14b8a6',
                        fontWeight: '700',
                        fontSize: '1.125rem',
                        minWidth: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'rgba(20, 184, 166, 0.2)',
                        borderRadius: '50%',
                        width: '32px',
                        height: '32px',
                        flexShrink: 0
                      }}>{idx + 1}</span>
                      <span>{takeaway}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* FAQ Section */}
              <h2 id="faq" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                Frequently Asked Questions
              </h2>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', marginBottom: '3rem' }}>
                {faqData.map((faq, idx) => (
                  <div key={idx} style={{
                    background: '#121212',
                    borderRadius: '12px',
                    padding: '1.5rem',
                    border: '1px solid #262626',
                    transition: 'border-color 0.3s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.borderColor = '#14b8a6'}
                  onMouseLeave={(e) => e.currentTarget.style.borderColor = '#262626'}>
                    <h3 style={{
                      color: '#ffffff',
                      fontSize: 'clamp(1.125rem, 2.5vw, 1.25rem)',
                      fontWeight: '600',
                      marginBottom: '0.75rem',
                      fontFamily: 'Sora, sans-serif',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '0.75rem'
                    }}>
                      <span style={{ color: '#14b8a6', flexShrink: 0 }}>Q{idx + 1}.</span>
                      <span>{faq.question}</span>
                    </h3>
                    <p style={{
                      color: '#e5e7eb',
                      fontSize: 'clamp(0.875rem, 2vw, 1rem)',
                      lineHeight: '1.75',
                      marginLeft: '2rem',
                      fontFamily: 'Inter, sans-serif'
                    }}>
                      {faq.answer}
                    </p>
                  </div>
                ))}
              </div>

              {/* CTA Section */}
              <div style={{
                marginTop: '3rem',
                background: '#121212',
                borderRadius: '16px',
                padding: 'clamp(1.5rem, 4vw, 2rem)',
                border: '1px solid #262626',
                position: 'relative',
                overflow: 'hidden'
              }}>
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '384px',
                  height: '384px',
                  background: 'rgba(20, 184, 166, 0.1)',
                  borderRadius: '50%',
                  filter: 'blur(80px)'
                }}></div>
                <div style={{ position: 'relative', zIndex: 10, textAlign: 'center' }}>
                  <h3 style={{
                    fontSize: 'clamp(1.25rem, 3vw, 1.5rem)',
                    fontWeight: '700',
                    color: '#ffffff',
                    marginBottom: '1rem',
                    fontFamily: 'Sora, sans-serif'
                  }}>
                    Ready to Transform Your Business?
                  </h3>
                  <p style={{
                    color: '#e5e7eb',
                    fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                    marginBottom: '1.5rem',
                    maxWidth: '600px',
                    margin: '0 auto 1.5rem',
                    fontFamily: 'Inter, sans-serif'
                  }}>
                    Contact us today for a personalized consultation and discover how we can help you achieve your goals.
                  </p>
                  <Link
                    to="/contact"
                    style={{
                      display: 'inline-block',
                      padding: 'clamp(0.625rem, 2vw, 0.75rem) clamp(1.25rem, 3vw, 1.5rem)',
                      border: '2px solid #14b8a6',
                      color: '#14b8a6',
                      borderRadius: '8px',
                      fontWeight: '600',
                      textDecoration: 'none',
                      transition: 'all 0.3s',
                      fontFamily: 'Inter, sans-serif',
                      fontSize: 'clamp(0.875rem, 2vw, 1rem)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#14b8a6';
                      e.currentTarget.style.color = '#ffffff';
                      e.currentTarget.style.boxShadow = '0 0 20px rgba(20, 184, 166, 0.25)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.color = '#14b8a6';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    Get Started Today
                  </Link>
                </div>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* Back to Top Button */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          style={{
            position: 'fixed',
            bottom: 'clamp(1.5rem, 4vw, 2rem)',
            right: 'clamp(1.5rem, 4vw, 2rem)',
            width: 'clamp(44px, 10vw, 48px)',
            height: 'clamp(44px, 10vw, 48px)',
            background: '#121212',
            border: '2px solid #262626',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#14b8a6',
            cursor: 'pointer',
            transition: 'all 0.3s',
            zIndex: 50
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = '#14b8a6';
            e.currentTarget.style.boxShadow = '0 0 20px rgba(20, 184, 166, 0.25)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#262626';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          <ArrowUp size={20} />
        </button>
      )}
    </div>
  );
};

export default Blog22022026;
